import React, { useState, useEffect, useCallback } from 'react';
import { compressImageForAnalysis } from '../../services/imageCompressionService';
import {
  Card,
  Stack,
  Button,
  ButtonGroup,
  TextContainer,
  Heading,
  Spinner,
  ProgressBar,
  Banner,
  Checkbox,
  Text
} from '@shopify/polaris';
import { useAuth } from '../../contexts/AuthContext';
import { useTemplate } from '../../contexts/TemplateContext';
import { useShop } from '../../contexts/ShopContext';
import { ImageSelector } from './ImageSelector';
import ResultPreviewPanel from './ResultPreviewPanel';
import { PersistentResultsSidebar } from './PersistentResultsSidebar';
import { getAIModelById } from '../../services/aiModelService';
import { deductCredits } from '../../services/optimizedCreditService';
import { getOpenAIApiKey, getProviderApiKey } from '../../services/apiKeyService';

/**
 * Component for executing Image Analyzing templates - Simplified Version
 * This version only supports:
 * 1. Featured image or manual selection
 * 2. Output to sidebar or append to description
 */
export const ImageTemplateExecutor = ({
  template,
  products,
  onUpdate,
  onSaveResults,
  onClose,
  onProgress // New prop for reporting progress to parent
}) => {
  const { user } = useAuth();
  const { currentShop } = useShop();
  const { handleTemplateUpdate } = useTemplate();

  // State for image selection and processing
  const [selectedImages, setSelectedImages] = useState({});
  const [error, setError] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [imageSelectionMode, setImageSelectionMode] = useState(
    template?.imageAnalysisMode === 'all' ? 'all' :
      template?.useFeaturedImage === true ? 'featured' : 'manual'
  );

  // Load the AI model details and initialize featured images if needed
  useEffect(() => {
    const loadModel = async () => {
      if (template?.modelId) {
        try {
          const model = await getAIModelById(template.modelId);
          setSelectedModel(model);
        } catch (error) {
          console.error('Error loading AI model:', error);
          setError('Failed to load AI model configuration');
        }
      }
    };

    loadModel();

    // Log the template configuration for debugging
    console.log('Template configuration loaded:', {
      id: template?.id,
      name: template?.name,
      sidebarOnly: template?.sidebarOnly,
      useFeaturedImage: template?.useFeaturedImage,
      imageAnalysisMode: template?.imageAnalysisMode
    });

    // Set mode from template config
    if (template?.imageAnalysisMode === 'all') {
      setImageSelectionMode('all');
    } else if (template?.useFeaturedImage === true) {
      setImageSelectionMode('featured');
    }
  }, [template]);

  // Auto-select images when products change based on mode
  useEffect(() => {
    if (products.length > 0) {
      if (imageSelectionMode === 'all') {
        console.log('Auto-selecting ALL images because mode is "all"');
        selectAllImages();
        // Auto-process if template config says so
        if (template?.imageAnalysisMode === 'all') {
          setTimeout(() => {
            setError('');
            processImages();
          }, 500);
        }
      } else if (imageSelectionMode === 'featured') {
        console.log('Auto-selecting featured images because mode is "featured"');
        selectFeaturedImages();
        if (template?.useFeaturedImage === true) {
          setTimeout(() => {
            setError('');
            processImages();
          }, 500);
        }
      }
    }
  }, [imageSelectionMode, products, template]);

  // Handle selecting an image for a product
  const handleSelectImage = (productId, image) => {
    setSelectedImages(prev => ({
      ...prev,
      [productId]: [image] // Store as array for consistency with multiple image selection
    }));
  };

  // Handle selecting multiple images for a product
  const handleSelectImages = (productId, images) => {
    setSelectedImages(prev => ({
      ...prev,
      [productId]: images
    }));
  };

  // Check if all products have selected images
  const allProductsHaveImages = useCallback(() => {
    // Auto modes don't need manual selection
    if (imageSelectionMode === 'featured' || imageSelectionMode === 'all') {
      return true;
    }

    // Otherwise, check if all products have selected images (at least one image per product)
    return products.every(product => {
      const productImages = selectedImages[product.id];
      return productImages && (Array.isArray(productImages) ? productImages.length > 0 : productImages);
    });
  }, [products, selectedImages, imageSelectionMode]);

  // Check if we should auto-process images
  const shouldAutoProcess = useCallback(() => {
    return (template?.useFeaturedImage === true && imageSelectionMode === 'featured') ||
      (template?.imageAnalysisMode === 'all' && imageSelectionMode === 'all');
  }, [template, imageSelectionMode]);

  // Auto-select featured images for all products
  const selectFeaturedImages = useCallback(() => {
    console.log('Selecting featured images for products:', products.length);
    const newSelectedImages = {};
    let allHaveImages = true;

    products.forEach(product => {
      const featuredImage = product.images?.find(img => img.position === 1) || product.images?.[0];
      if (featuredImage) {
        newSelectedImages[product.id] = [featuredImage];
      } else {
        console.warn(`No images found for product ${product.id}`);
        allHaveImages = false;
      }
    });

    setSelectedImages(newSelectedImages);
    if (!allHaveImages) {
      setError('Some products do not have images.');
    } else {
      setError('');
    }
    return allHaveImages;
  }, [products]);

  // Auto-select ALL images for each product (Analyze All mode)
  const selectAllImages = useCallback(() => {
    console.log('Selecting ALL images for products:', products.length);
    const newSelectedImages = {};
    let allHaveImages = true;

    products.forEach(product => {
      const images = product.images || [];
      if (images.length > 0) {
        newSelectedImages[product.id] = images.map(img => ({
          id: img.id,
          url: img.src || img.url,
          alt: img.alt || '',
          position: img.position || 0
        }));
        console.log(`Selected ${images.length} images for product ${product.id}`);
      } else {
        console.warn(`No images found for product ${product.id}`);
        allHaveImages = false;
      }
    });

    setSelectedImages(newSelectedImages);
    if (!allHaveImages) {
      setError('Some products do not have images.');
    } else {
      setError('');
    }
    return allHaveImages;
  }, [products]);

  // Process the selected images with the AI model
  const processImages = async () => {
    // Validate prerequisites - skip validation if we're auto-processing with featured images
    if (!shouldAutoProcess() && !allProductsHaveImages()) {
      setError('Please select images for all products');
      return;
    }

    // Validate that we have a selected model
    if (!selectedModel) {
      setError('No AI model selected. Please try again or contact support.');
      return;
    }

    // Log user info for debugging
    console.log('Processing images with user:', user?.uid);

    // Start processing
    setError('');

    // If not manual mode, select images automatically
    if (imageSelectionMode === 'all') {
      selectAllImages();
    } else if (imageSelectionMode === 'featured') {
      selectFeaturedImages();
    }

    // Calculate credit cost — for "all" mode, cost is per IMAGE not per product
    const totalProducts = products.length;
    const costPerOutput = selectedModel.creditCostPerOutput || 1;
    let totalCreditCost;
    if (imageSelectionMode === 'all') {
      // Count total images across all products
      const totalImages = products.reduce((sum, p) => sum + (selectedImages[p.id]?.length || 0), 0);
      totalCreditCost = totalImages * costPerOutput;
      console.log(`Analyze All mode: ${totalImages} total images × ${costPerOutput} = ${totalCreditCost} credits`);
    } else {
      totalCreditCost = totalProducts * costPerOutput;
    }

    // Notify parent of initial progress (0%)
    if (onProgress) {
      onProgress(0);
    }

    // Close the modal immediately after starting processing
    // This allows the parent component to handle the processing UI
    if (onClose) {
      onClose();
    }

    // Continue processing in the background after modal is closed
    // We need to call processSelectedImages directly to ensure it runs
    setTimeout(async () => {
      try {
        await processSelectedImages(totalProducts, totalCreditCost);
      } catch (error) {
        console.error('Error processing images after modal close:', error);
        // Make sure parent knows processing is complete even if there was an error
        if (onProgress) {
          // Ensure progress is exactly 100% when complete
          onProgress(100);
          console.log('Image template processing complete, setting progress to exactly 100%');
        }
      }
    }, 100);
  };

  // Helper: Make a Vision API call for a single image
  const callVisionAPI = async (imageUrl, promptText) => {
    // Compress image before sending to API (1024px max, JPEG 85%)
    const compressedUrl = await compressImageForAnalysis(imageUrl, 1024);
    
    const messages = [
      {
        role: 'system',
        content: 'You are an AI assistant that analyzes product images for e-commerce. You MUST ALWAYS respond with a valid JSON object. Never respond with plain text, markdown, or code blocks. Follow the user\'s analysis instructions and put your analysis into the JSON fields specified in the prompt.'
      },
      {
        role: 'user',
        content: [
          {
            type: 'image_url',
            image_url: { url: compressedUrl, detail: 'high' }
          },
          { type: 'text', text: promptText }
        ]
      }
    ];

    let jsonResponse;

    if (selectedModel.provider === 'openai') {
      const apiKey = await getOpenAIApiKey();
      if (!apiKey) throw new Error('OpenAI API key is required for image analysis');

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: selectedModel.modelId || 'gpt-4-vision-preview',
          messages,
          temperature: 0.7,
          max_tokens: 2000,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API request failed: ${response.status} ${response.statusText}`);
      }
      jsonResponse = await response.json();

    } else if (selectedModel.provider === 'gemini') {
      const { generateContent } = await import('../../services/aiProviderService');
      const modelIdentifier = selectedModel.id || selectedModel.modelId || selectedModel.internalName;
      if (!modelIdentifier) throw new Error('No valid model identifier found');

      jsonResponse = await generateContent(modelIdentifier, {
        messages,
        temperature: 0.7,
        max_tokens: 2000,
        images: [compressedUrl],
        modelType: 'vision'
      });
    } else {
      throw new Error(`Unsupported provider: ${selectedModel.provider}`);
    }

    // Extract content from response
    const content = jsonResponse?.choices?.[0]?.message?.content || '';
    return content;
  };

  // Helper: Parse JSON from Vision API response
  const parseVisionResponse = (content) => {
    if (!content) return { description: '' };
    try {
      const cleaned = content.replace(/```json|```/g, '').trim();
      return JSON.parse(cleaned);
    } catch {
      try {
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) return JSON.parse(jsonMatch[0]);
      } catch { }
      return { description: content };
    }
  };

  // Process the selected images and report progress
  const processSelectedImages = async (totalProductCount, totalCredits) => {
    const newResults = [];

    try {
      if (onProgress) onProgress(5);

      console.log('Template sidebarOnly setting:', template.sidebarOnly);
      console.log('Image selection mode:', imageSelectionMode);

      // Prepare execution metadata for credit tracking
      const executionMetadata = {
        templateId: template.id,
        templateName: template.name,
        modelId: selectedModel.id,
        modelName: selectedModel.displayName,
        productCount: totalProductCount,
        creditCost: totalCredits,
        shopId: currentShop?.id,
        imageSelectionMode
      };

      // Deduct credits before processing
      await deductCredits(user?.uid, totalCredits, executionMetadata);

      // ===== "ANALYZE ALL" MODE =====
      if (imageSelectionMode === 'all') {
        console.log('=== ANALYZE ALL IMAGES MODE ===');
        const totalProducts = products.length;
        let totalSteps = 0;
        let completedSteps = 0;

        // Count total steps for progress
        products.forEach(p => { totalSteps += (selectedImages[p.id]?.length || 0); });

        for (let i = 0; i < totalProducts; i++) {
          const product = products[i];
          const images = selectedImages[product.id] || [];

          if (images.length === 0) {
            console.warn(`No images for product ${product.id}, skipping`);
            continue;
          }

          console.log(`Analyzing ${images.length} images for product: ${product.title}`);
          const imageAnalysisResults = [];

          // Process each image individually
          for (let j = 0; j < images.length; j++) {
            const image = images[j];
            try {
              console.log(`  Analyzing image ${j + 1}/${images.length}: ${image.url.substring(0, 60)}...`);

              // Build a prompt that enforces JSON output with suggestedAltText
              const analysisPrompt = `Product: ${product.title || 'Unknown'} (Image ${j + 1} of ${images.length})

Analyze this image and respond as JSON. Put your analysis into the "description" field.

{
  "description": "Your detailed analysis of what is shown in this image",
  "category": "one of: hero, detail, lifestyle, variant, scale, texture",
  "suggestedAltText": "Short, unique, descriptive alt text for THIS image (max 125 chars) - describe what is visible",
  "bestPosition": "one of: top, middle, bottom, gallery"
}`;

              const rawContent = await callVisionAPI(image.url, analysisPrompt);
              const parsed = parseVisionResponse(rawContent);

              imageAnalysisResults.push({
                imageIndex: j + 1,
                imageUrl: image.url,
                imageId: image.id,
                description: parsed.description || rawContent?.substring(0, 200) || '',
                category: parsed.category || 'detail',
                suggestedAltText: parsed.suggestedAltText || parsed.suggestedalttext || parsed.alt_text || `${product.title || 'Product'} - Image ${j + 1}`,
                bestPosition: parsed.bestPosition || parsed.bestposition || 'middle',
                confidence: parsed.confidence || 0.8
              });

              console.log(`  ✓ Image ${j + 1} analyzed: [${parsed.category || 'detail'}] ${(parsed.description || '').substring(0, 60)}...`);

            } catch (imgError) {
              console.error(`  ✗ Error analyzing image ${j + 1}:`, imgError.message);
              imageAnalysisResults.push({
                imageIndex: j + 1,
                imageUrl: image.url,
                imageId: image.id,
                description: 'Analysis failed',
                category: 'detail',
                suggestedAltText: `${product.title || 'Product'} - image ${j + 1} analysis failed`,
                bestPosition: 'gallery',
                confidence: 0,
                error: imgError.message
              });
            }

            // Update progress
            completedSteps++;
            if (onProgress) {
              const progressPercent = Math.min(5 + (completedSteps / totalSteps) * 90, 95);
              onProgress(progressPercent);
            }
          }

          console.log(`Analysis complete for ${product.title}: ${imageAnalysisResults.length} images analyzed`);

          // Format combined analysis content for sidebar/description
          let analysisSummary = `<h3>Image Analysis Results (${imageAnalysisResults.length} images)</h3>\n`;
          imageAnalysisResults.forEach(analysis => {
            analysisSummary += `<p><strong>Image ${analysis.imageIndex} [${analysis.category}]:</strong> ${analysis.description}</p>\n`;
            analysisSummary += `<p><em>Alt text:</em> ${analysis.suggestedAltText}</p>\n`;
            analysisSummary += `<p><em>Best position:</em> ${analysis.bestPosition}</p>\n<hr>\n`;
          });

          // Create result object
          const result = {
            id: product.id,
            productName: product.title || `Product ${i + 1}`,
            templateName: template.name,
            imageUrl: images[0]?.url,
            status: 'success',
            fields: { description: analysisSummary },
            insertModes: {},
            imageAnalysis: imageAnalysisResults // Structured analysis data
          };

          // Pass analysis results to parent via onUpdate
          if (onUpdate) {
            const updateData = {
              _imageAnalysis: imageAnalysisResults // Special key for downstream use
            };
            if (!template.sidebarOnly) {
              updateData.body_html = analysisSummary;
            }
            onUpdate(product.id, updateData);
            console.log(`Passed ${imageAnalysisResults.length} image analysis results to parent for product ${product.id}`);
          }

          newResults.push(result);
        }

      } else {
        // ===== EXISTING MODE (Featured / Manual) =====

        // Process each product
        const totalProducts = products.length;
        for (let i = 0; i < totalProducts; i++) {
          const product = products[i];
          const images = selectedImages[product.id];

          if (!images) {
            throw new Error(`No images selected for product ${product.title || product.id}`);
          }

          try {
            // Replace any placeholders in the prompt with product data
            let processedPrompt = template.prompt;

            Object.entries(product).forEach(([key, value]) => {
              if (typeof value === 'string') {
                processedPrompt = processedPrompt.replace(new RegExp(`\{${key}\}`, 'g'), value);
              }
            });

            // Get all outputs (fields and metafields) for structured response
            const allOutputs = [...(template.outputFields || [])];

            // Add metafields to allOutputs if any
            if (template.outputMetafields && template.outputMetafields.length > 0) {
              template.outputMetafields.forEach(metafieldItem => {
                let metafieldId = '';
                if (typeof metafieldItem === 'string') {
                  metafieldId = metafieldItem;
                } else if (typeof metafieldItem === 'object' && metafieldItem !== null) {
                  if (metafieldItem.namespace && metafieldItem.key) {
                    metafieldId = `metafield.${metafieldItem.namespace}.${metafieldItem.key}`;
                  }
                }
                if (metafieldId && !allOutputs.includes(metafieldId)) {
                  allOutputs.push(metafieldId);
                }
              });
            }

            const formattedOutputs = allOutputs.map(field => field);

            const enhancedPrompt = `${processedPrompt}

IMPORTANT FORMATTING INSTRUCTIONS:
Respond with a JSON object that has EXACTLY this structure:
{
  "${formattedOutputs[0]}":"A single plain text string with all the information combined"
}

Your response MUST be a single text string inside the JSON value, NOT a nested object or array. All information should be combined into ONE CONTINUOUS TEXT STRING.

Example of CORRECT format:
{ "${formattedOutputs[0]}": "White cropped cardigan with long bell sleeves and front tie closure, made of ribbed knit fabric." }

Example of INCORRECT format:
{ "${formattedOutputs[0]}": { "product_type": "Women's cropped cardigan", "color": "White" } }`;

            const promptText = `Product Title: ${product.title || 'No title available'}\n\n${enhancedPrompt}`;

            // For featured/manual mode, send all selected images at once
            const rawContent = await callVisionAPI(images[0].url, promptText);
            const parsedResponse = parseVisionResponse(rawContent);

            // Create a result object
            const result = {
              id: product.id,
              productName: product.title || `Product ${i + 1}`,
              templateName: template.name,
              imageUrl: images[0].url,
              status: 'success',
              fields: {},
              insertModes: {}
            };

            // Get the content from the API response
            const generatedContent = parsedResponse.description ||
              (Object.keys(parsedResponse).length > 0 ? parsedResponse[Object.keys(parsedResponse)[0]] : null) ||
              rawContent;

            let safeContent = '';
            if (typeof generatedContent === 'string') {
              if (generatedContent.trim().startsWith('{') && generatedContent.trim().endsWith('}')) {
                try {
                  safeContent = formatJsonForDescription(JSON.parse(generatedContent));
                } catch { safeContent = generatedContent; }
              } else {
                safeContent = generatedContent;
              }
            } else if (typeof generatedContent === 'object' && generatedContent !== null) {
              safeContent = formatJsonForDescription(generatedContent);
            } else {
              safeContent = JSON.stringify(generatedContent);
            }

            result.fields.description = safeContent;

            if (!template.sidebarOnly) {
              if (onUpdate) {
                onUpdate(product.id, { body_html: safeContent });
              }
            }

            newResults.push(result);

          } catch (productError) {
            console.error(`Error processing product ${product.id}:`, productError);

            // Add error result
            newResults.push({
              id: product.id,
              productName: product.title || `Product ${i + 1}`,
              templateName: template.name,
              imageUrl: images ? images[0].url : 'unknown',
              status: 'error',
              error: productError.message,
              fields: {}
            });
          }

          // Report progress to parent component
          if (onProgress) {
            // Calculate progress and ensure it never exceeds 100%
            const progressPercent = Math.min(((i + 1) / totalProducts) * 100, 100);
            console.log(`Image template progress: ${i + 1}/${totalProducts} = ${progressPercent.toFixed(1)}%`);
            onProgress(progressPercent);
          }
        }

      } // end else (Featured / Manual mode)

      console.log('Processing complete, results:', newResults);

      // Directly save results to the parent component's sidebar
      await saveResultsToSidebar(newResults);

      // CRITICAL: Signal completion to the parent component with exactly 100% progress
      if (onProgress) {
        console.log('Setting final progress to exactly 100% to ensure loading indicator completes');
        // Force progress to exactly 100% to ensure the loading indicator completes
        onProgress(100);

        // Send the 100% progress signal again after a short delay to ensure it's received
        setTimeout(() => {
          console.log('Sending final 100% progress signal again after delay');
          onProgress(100);
        }, 500);
      }

      // Return the results
      return newResults;
    } catch (error) {
      console.error('Error processing images:', error);
      setError(`Error processing images: ${error.message}`);
      // Processing state is now handled by the parent component
      return [];
    }
  };

  // Helper function to format JSON for human-readable description
  const formatJsonForDescription = (jsonObj) => {
    let formattedContent = '';

    try {
      // Handle arrays specially
      if (Array.isArray(jsonObj)) {
        return jsonObj.map(item => {
          if (typeof item === 'object' && item !== null) {
            return formatJsonForDescription(item);
          }
          return item;
        }).join('<br>• ');
      }

      // Format each key-value pair in the JSON object
      Object.entries(jsonObj).forEach(([key, value]) => {
        // Skip empty values
        if (value === null || value === undefined || value === '') return;

        // Format the key for display
        const formattedKey = key
          .replace(/([A-Z])/g, ' $1') // Add spaces before capital letters
          .replace(/_/g, ' ')        // Replace underscores with spaces
          .trim()                    // Remove leading/trailing spaces
          .replace(/^\w/, c => c.toUpperCase()); // Capitalize first letter

        // Handle different value types
        if (Array.isArray(value)) {
          // Format arrays as bullet points
          formattedContent += `<strong>${formattedKey}:</strong><br>• ${value.join('<br>• ')}<br><br>`;
        } else if (typeof value === 'object' && value !== null) {
          // Recursively format nested objects
          formattedContent += `<strong>${formattedKey}:</strong><br>${formatJsonForDescription(value)}<br>`;
        } else {
          // Simple key-value pair
          formattedContent += `<strong>${formattedKey}:</strong> ${value}<br><br>`;
        }
      });

      return formattedContent;
    } catch (e) {
      console.error('Error formatting JSON:', e);
      return JSON.stringify(jsonObj);
    }
  };

  // After processing images, directly save results to the parent component's sidebar
  const saveResultsToSidebar = async (results) => {
    try {
      console.log('Directly saving results to persistent sidebar:', results);

      // Only save to sidebar if sidebarOnly is true or there was an error
      // This ensures the sidebar doesn't open for 'Add to Description' mode unless there's an error
      if (onSaveResults && (template.sidebarOnly === true || results.some(r => r.status === 'error'))) {
        console.log('Saving results to sidebar because template.sidebarOnly =', template.sidebarOnly === true);
        await onSaveResults(results);
      } else {
        console.log('Skipping sidebar save because template.sidebarOnly =', Boolean(template.sidebarOnly));
      }
    } catch (error) {
      console.error('Error saving results to sidebar:', error);
      setError(`Error saving results: ${error.message}`);
    }
  };

  return (
    <Stack vertical spacing="tight">
      {error && (
        <Banner status="critical">
          <p>{error}</p>
        </Banner>
      )}

      <Card sectioned>
        <TextContainer>
          <Heading>Image Analyzing Template: {template?.name}</Heading>
          <Text variant="bodyMd">
            {imageSelectionMode === 'all'
              ? 'All product images will be analyzed individually. This generates detailed descriptions, categories, and alt text for each image.'
              : imageSelectionMode === 'featured'
                ? 'Using featured images for all products. The AI will analyze these images automatically.'
                : 'Select an image for each product to analyze with the AI model.'}
          </Text>

          {imageSelectionMode === 'manual' && (
            <Banner status="info">
              <p><strong>Workflow Note:</strong> Manual image templates are always processed first in a workflow. Enable "Featured" or "All Images" mode for automatic sequential processing.</p>
            </Banner>
          )}

          {imageSelectionMode === 'all' && (
            <Banner status="warning">
              <p><strong>Credit Cost:</strong> Analyze All Images mode costs 1 credit per image. Products with many images will consume more credits.
                {products.length > 0 && ` (${products.reduce((sum, p) => sum + (p.images?.length || 0), 0)} total images across ${products.length} product${products.length > 1 ? 's' : ''})`}
              </p>
            </Banner>
          )}

          <div style={{ marginTop: '12px' }}>
            <Text variant="headingSm" as="h3">Image Selection Mode</Text>
            <Stack vertical spacing="tight">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 0' }}>
                <input
                  type="radio"
                  name="imageSelectionMode"
                  value="manual"
                  checked={imageSelectionMode === 'manual'}
                  onChange={() => setImageSelectionMode('manual')}
                />
                <span><strong>Manual Selection</strong> — Choose specific images for each product</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 0' }}>
                <input
                  type="radio"
                  name="imageSelectionMode"
                  value="featured"
                  checked={imageSelectionMode === 'featured'}
                  onChange={() => { setImageSelectionMode('featured'); selectFeaturedImages(); }}
                />
                <span><strong>Featured Image</strong> — Automatically use the first/featured image</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 0' }}>
                <input
                  type="radio"
                  name="imageSelectionMode"
                  value="all"
                  checked={imageSelectionMode === 'all'}
                  onChange={() => { setImageSelectionMode('all'); selectAllImages(); }}
                />
                <span><strong>Analyze All Images</strong> — Analyze each image individually (generates alt text + descriptions)</span>
              </label>
            </Stack>
          </div>
        </TextContainer>
      </Card>

      {/* Only show image selection UI in manual mode */}
      {imageSelectionMode === 'manual' && products.map(product => (
        <Card key={product.id} sectioned>
          <Stack vertical spacing="tight">
            <Heading>{product.title || 'Product'}</Heading>
            <ImageSelector
              product={product}
              selectedImages={selectedImages[product.id]}
              onSelectImage={(image) => handleSelectImage(product.id, image)}
              onSelectImages={(images) => handleSelectImages(product.id, images)}
              maxImages={selectedModel?.maxImages || (selectedModel?.provider === 'gemini' ? 3 : 1)}
            />
          </Stack>
        </Card>
      ))}

      {/* Show the process button (hidden for auto-process modes) */}
      {!shouldAutoProcess() && (
        <Card>
          <Card.Section>
            <Stack distribution="equalSpacing" alignment="center">
              <Stack.Item>
                {/* Processing UI is now handled by the parent component */}
              </Stack.Item>
              <Stack.Item>
                <ButtonGroup>
                  <Button onClick={() => onClose && onClose()}>Cancel</Button>
                  <Button
                    primary
                    onClick={processImages}
                    disabled={!allProductsHaveImages()}
                  >
                    {imageSelectionMode === 'all' ? 'Analyze All Images' : 'Process Images'}
                  </Button>
                </ButtonGroup>
              </Stack.Item>
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* No processing indicators in the modal - processing UI is handled by parent component */}

      {/* Results are now managed by the parent component's persistent sidebar */}
    </Stack>
  );
};

export default ImageTemplateExecutor;
